/*
 * Vitrine — generative backdrop. A slow, flowing "silk / aurora" haze rendered on
 * a sky-dome behind the shoe, tinted with the currently-selected colours and kept
 * deliberately subtle (mostly the bone base, a wash of colour on top) so the shoe
 * stays the hero. Shared by the single-model viewer and the design carousel.
 *
 *   const b = VitrineBackdrop.create({ base: '#ECEAE5' });
 *   scene.add(b.object);
 *   b.setColors(a, b, c, baseHex);   // hex strings; eased in over a few frames
 *   b.update(dt);                    // call each frame
 */
(function (w) {
  'use strict';
  if (typeof THREE === 'undefined') { return; }

  var VERT = [
    'varying vec3 vDir;',
    'void main() {',
    '  vDir = normalize(position);',
    '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
    '}'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'varying vec3 vDir;',
    'uniform float uTime;',
    'uniform vec3 uColorA;',
    'uniform vec3 uColorB;',
    'uniform vec3 uColorC;',
    'uniform vec3 uBase;',
    'float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }',
    'float noise(vec2 p){',
    '  vec2 i = floor(p); vec2 f = fract(p);',
    '  float a = hash(i);',
    '  float b = hash(i + vec2(1.0, 0.0));',
    '  float c = hash(i + vec2(0.0, 1.0));',
    '  float d = hash(i + vec2(1.0, 1.0));',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);',
    '}',
    'float fbm(vec2 p){',
    '  float v = 0.0; float a = 0.5;',
    '  for (int i = 0; i < 4; i++){ v += a * noise(p); p *= 2.0; a *= 0.5; }',
    '  return v;',
    '}',
    'void main(){',
    '  vec2 uv = vec2(atan(vDir.z, vDir.x) * 0.15915494, vDir.y * 0.5 + 0.5);',
    '  float t = uTime * 0.025;',
    '  vec2 q = vec2(fbm(uv * 3.0 + t), fbm(uv * 3.0 + vec2(5.2, 1.3) - t));',
    '  float n = fbm(uv * 4.0 + q * 1.6 + t * 0.6);',
    '  float m = fbm(uv * 2.0 - t * 0.8);',
    '  float ribbon = sin((uv.x + q.x * 0.18) * 22.0 + (uv.y - t * 1.4) * 5.0);',
    '  ribbon += 0.55 * sin((uv.x - q.y * 0.14) * 37.0 - (uv.y + t * 1.1) * 4.0);',
    '  ribbon = smoothstep(-0.35, 0.85, ribbon) - 0.5;',
    '  float veil = fbm(vec2(uv.x * 8.0 + q.x * 1.2, uv.y * 2.2 - t * 1.4));',
    '  vec3 col = mix(uColorA, uColorB, smoothstep(0.15, 0.85, n));',
    '  col = mix(col, uColorC, smoothstep(0.4, 0.95, m));',
    '  col = mix(uBase, col, 0.34 + 0.38 * n);',           // bone base, a richer wash of colour
    '  col += ribbon * 0.06;',
    '  col += (veil - 0.5) * 0.045;',
    '  float grad = smoothstep(-0.4, 1.0, vDir.y);',        // top a touch richer than the floor
    '  col = mix(col, col + uColorC * 0.16, grad);',
    '  col = clamp(col, 0.0, 1.0);',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  function color(c, fallback) {
    try { return new THREE.Color(c || fallback); } catch (e) { return new THREE.Color(fallback); }
  }

  function create(opts) {
    opts = opts || {};
    var uniforms = {
      uTime:   { value: 0 },
      uColorA: { value: color(opts.a, '#8B3A1E') },
      uColorB: { value: color(opts.b, '#5A5750') },
      uColorC: { value: color(opts.c, '#C8945A') },
      uBase:   { value: color(opts.base, '#ECEAE5') }
    };
    var mat = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false
    });
    var geo  = new THREE.SphereGeometry(40, 48, 32);
    var mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder   = -1;   // always behind everything
    mesh.frustumCulled = false;

    // Targets the live uniforms ease toward — so a colourway change melts rather
    // than snaps.
    var targetA = uniforms.uColorA.value.clone();
    var targetB = uniforms.uColorB.value.clone();
    var targetC = uniforms.uColorC.value.clone();

    function setColors(a, b, c, baseHex) {
      if (a) targetA.set(a);
      if (b) targetB.set(b);
      if (c) targetC.set(c);
      if (baseHex) uniforms.uBase.value.set(baseHex);
    }
    function update(dt) {
      uniforms.uTime.value += dt || 0.016;
      uniforms.uColorA.value.lerp(targetA, 0.04);
      uniforms.uColorB.value.lerp(targetB, 0.04);
      uniforms.uColorC.value.lerp(targetC, 0.04);
    }
    function dispose() { geo.dispose(); mat.dispose(); }

    return { object: mesh, setColors: setColors, update: update, dispose: dispose };
  }

  w.VitrineBackdrop = { create: create };
})(window);
